import { Button, Divider } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import event from '~helpers/mixpanel';
import hexToRGBA from '~helpers/hex-to-rgba';
import SvgFormat from '~helpers/svgFormat';
import OnboardingContext, { onboardingProcesses } from '../context';

const useStylesProgressBar = makeStyles(theme => ({
  progressBar: ({ processCount }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    display: 'inline-block',
    width: processCount === 6 ? '100%' : `calc(16.6% * ${processCount})`,
    height: processCount === 7 ? 0 : '6px',
    backgroundColor: theme.colors.mainGreen6,
    transition: 'width 1s',
  }),
}));

const processCount = {
  PROPERTY_TYPE: 1,
  RESIDENT_TYPE: 2,
  PROPERTY_ADDRESS: 3,
  PROPERTY_NAME: 4,
  COVER_PHOTO: 5,
  BUSINESS_INFO: 6,
  SUCCESSFUL: 7,
};

export const processEventNameMap = {
  PROPERTY_TYPE: 'Clicked OP Property Type Page',
  RESIDENT_TYPE: 'Clicked OP Resident Type Page',
  PROPERTY_ADDRESS: 'Clicked OP Property Map Page',
  PROPERTY_NAME: 'Clicked OP Create Name Page',
  COVER_PHOTO: 'Clicked OP Cover Photo Page',
  BUSINESS_INFO: 'ClickedOP Business Info Page',
  SUCCESSFUL: 'Clicked OP Finishing Page',
  RECALLED_TO: 'Clicked OP Return Page',
};

export const ProgressBar = ({ count }) => {
  const onboardingData = useContext(OnboardingContext);
  const classes = useStylesProgressBar({
    processCount: count ?? processCount[onboardingData?.process],
  });

  return <div className={classes.progressBar} />;
};

ProgressBar.propTypes = {
  count: PropTypes.number,
};

const useStyles = makeStyles(theme => ({
  wrap: {
    ...theme.flex.mainSpaceBetween,
    position: 'fixed',
    top: 0,
    width: '100%',
    height: 80,
    padding: '0 48px',
    boxShadow: theme.shadow.componentShallow,
    backgroundColor: theme.colors.gray1,
    zIndex: 1001,
  },
  logo: {
    width: '188px',
    height: '32px',
    fill: theme.colors.yellow6,
  },
  progressBar: ({ process }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    display: 'inline-block',
    width: process === 6 ? '100%' : `calc(16.6% * ${process})`,
    height: process === 7 ? 0 : '6px',
    backgroundColor: theme.colors.mainGreen6,
    transition: 'width 1s',
  }),
  logoWrap: {
    ...theme.flex.center,
  },
  header: {
    ...theme.typography.h3,
    color: hexToRGBA(theme.colors.gray10, 0.45),
  },
  divider: {
    width: 2,
    height: 24,
    marginLeft: 16,
    marginRight: 16,
    backgroundColor: theme.colors.gray6,
  },
  helpWrap: {
    ...theme.flex.center,
    color: theme.colors.captionColor,
  },
  emailBtn: {
    ...theme.flex.center,
    ...theme.typography.h4,
    border: `1px solid ${theme.colors.gray5} !important`,
    marginLeft: '8px',
    color: `${theme.colors.bodyColor} !important`,
    boxShadow: 'none',
  },
  iconEmail: {
    width: '12px',
    height: '12px',
    marginRight: '10px',
  },
}));

const Header = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const onboardingData = useContext(OnboardingContext);
  const { process, propertyTypeSlug, residentTypeSlug } = onboardingData;
  const eventObj = {
    'Clicked Position': 'Email Btn',
  };
  if (process !== onboardingProcesses.RECALLED_TO && process !== onboardingProcesses.SUCCESSFUL) {
    Object.assign(eventObj, { 'Property Type': propertyTypeSlug });
  }
  if (process === onboardingProcesses.RESIDENT_TYPE) {
    Object.assign(eventObj, {
      'Resident Type': t(`landlord.onboarding.resident_type.${residentTypeSlug.toLowerCase()}`),
    });
  }
  const handleClick = () => {
    event(processEventNameMap[process], eventObj);
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.logoWrap}>
        <SvgFormat className={classes.logo} type="logo" />
        <Divider type="vertical" className={classes.divider} />
        <span className={classes.header}>{t('landlord.onboarding.header.title')}</span>
      </div>
      <div className={classes.helpWrap}>
        {t('landlord.onboarding.header.help')}
        <a target="_blank" href="mailto:contact@student.com" rel="noreferrer">
          <Button
            size="large"
            type="primary"
            ghost
            className={classes.emailBtn}
            onClick={handleClick}
          >
            <SvgFormat className={classes.iconEmail} type="progress-check-your-email" />
            {t('landlord.onboarding.btn.email_us')}
          </Button>
        </a>
      </div>
      {![onboardingProcesses.RECALLED_TO, onboardingProcesses.SUCCESSFUL].includes(process) && (
        <ProgressBar />
      )}
    </div>
  );
};

export default Header;
